<script setup>
import {
  onMounted,
  onBeforeUnmount,
  computed,
  ref,
  shallowRef,
  toRefs,
  nextTick,
} from 'vue';
import { useMessageSelection } from 'dashboard/composables/useMessageSelection.js';
import { useTimeoutFn } from '@vueuse/core';
import { provideMessageContext } from './provider.js';
import { isUnattributedWameMessage } from './useDisplaySenderName.js';
import { stripGroupSenderPrefix } from './groupParticipant.js';
import { useTrack } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import { useStore } from 'vuex';
import { emitter } from 'shared/helpers/mitt';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { LocalStorage } from 'shared/helpers/localStorage';
import { ACCOUNT_EVENTS } from 'dashboard/helper/AnalyticsHelper/events';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import { getInboxIconByType } from 'dashboard/helper/inbox';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import {
  MESSAGE_TYPES,
  ATTACHMENT_TYPES,
  MESSAGE_VARIANTS,
  SENDER_TYPES,
  ORIENTATION,
  MESSAGE_STATUS,
  CONTENT_TYPES,
} from './constants';

import Avatar from 'next/avatar/Avatar.vue';

import TextBubble from './bubbles/Text/Index.vue';
import ActivityBubble from './bubbles/Activity.vue';
import ImageBubble from './bubbles/Image.vue';
import ImageAlbumBubble from './bubbles/ImageAlbum.vue';
import StickerAlbumBubble from './bubbles/StickerAlbum.vue';
import FileBubble from './bubbles/File.vue';
import AudioBubble from './bubbles/Audio.vue';
import VideoBubble from './bubbles/Video.vue';
import EmbedBubble from './bubbles/Embed.vue';
import InstagramStoryBubble from './bubbles/InstagramStory.vue';
import EmailBubble from './bubbles/Email/Index.vue';
import UnsupportedBubble from './bubbles/Unsupported.vue';
import ContactBubble from './bubbles/Contact.vue';
import DyteBubble from './bubbles/Dyte.vue';
import LocationBubble from './bubbles/Location.vue';
import CSATBubble from './bubbles/CSAT.vue';
import FormBubble from './bubbles/Form.vue';
import VoiceCallBubble from './bubbles/VoiceCall.vue';
import WameMediaProgress from './bubbles/WameMediaProgress.vue';
// Aceleres: bolha de possivel falta pausada temporariamente.
// import AceleresPossibleAbsenceAlert from './bubbles/AceleresPossibleAbsenceAlert.vue';

import MessageError from './MessageError.vue';
import ContextMenu from 'dashboard/modules/conversations/components/MessageContextMenu.vue';
import {
  findContextMenuTrigger,
  getDashboardZoom,
  fixedPosition,
  computeMenuAnchor,
} from './contextMenuAnchor.js';
import MessageReactions from './MessageReactions.vue';
import MessageReactionsModal from './MessageReactionsModal.vue';
import MessageReactionBar from 'dashboard/components/widgets/conversation/MessageReactionBar.vue';
import { useBranding } from 'shared/composables/useBranding';

/**
 * @typedef {Object} Attachment
 * @property {number} id - Unique identifier for the attachment
 * @property {number} messageId - ID of the associated message
 * @property {'image'|'audio'|'video'|'file'|'location'|'fallback'|'share'|'story_mention'|'contact'|'ig_reel'} fileType - Type of the attachment (file or image)
 * @property {number} accountId - ID of the associated account
 * @property {string|null} extension - File extension
 * @property {string} dataUrl - URL to access the full attachment data
 * @property {string} thumbUrl - URL to access the thumbnail version
 * @property {number} fileSize - Size of the file in bytes
 * @property {number|null} width - Width of the image if applicable
 * @property {number|null} height - Height of the image if applicable
 */

/**
 * @typedef {Object} Sender
 * @property {Object} additional_attributes - Additional attributes of the sender
 * @property {Object} custom_attributes - Custom attributes of the sender
 * @property {string} email - Email of the sender
 * @property {number} id - ID of the sender
 * @property {string|null} identifier - Identifier of the sender
 * @property {string} name - Name of the sender
 * @property {string|null} phone_number - Phone number of the sender
 * @property {string} thumbnail - Thumbnail URL of the sender
 * @property {string} type - Type of sender
 */

/**
 * @typedef {Object} ContentAttributes
 * @property {string} externalError - an error message to be shown if the message failed to send
 */

/**
 * @typedef {Object} Props
 * @property {('sent'|'delivered'|'read'|'failed'|'progress')} status - The delivery status of the message
 * @property {ContentAttributes} [contentAttributes={}] - Additional attributes of the message content
 * @property {Attachment[]} [attachments=[]] - The attachments associated with the message
 * @property {Sender|null} [sender=null] - The sender information
 * @property {boolean} [private=false] - Whether the message is private
 * @property {number|null} [senderId=null] - The ID of the sender
 * @property {number} createdAt - Timestamp when the message was created
 * @property {number} currentUserId - The ID of the current user
 * @property {number} id - The unique identifier for the message
 * @property {number} messageType - The type of message (must be one of MESSAGE_TYPES)
 * @property {string|null} [error=null] - Error message if the message failed to send
 * @property {string|null} [senderType=null] - The type of the sender
 * @property {string} content - The message content
 * @property {boolean} [groupWithNext=false] - Whether the message should be grouped with the next message
 * @property {Object|null} [inReplyTo=null] - The message to which this message is a reply
 * @property {boolean} [isEmailInbox=false] - Whether the message is from an email inbox
 * @property {number} conversationId - The ID of the conversation to which the message belongs
 * @property {number} inboxId - The ID of the inbox to which the message belongs
 */

// eslint-disable-next-line vue/define-macros-order
const props = defineProps({
  id: { type: Number, required: true },
  messageType: {
    type: Number,
    required: true,
    validator: value => Object.values(MESSAGE_TYPES).includes(value),
  },
  status: {
    type: String,
    required: true,
    validator: value => Object.values(MESSAGE_STATUS).includes(value),
  },
  attachments: { type: Array, default: () => [] },
  content: { type: String, default: null },
  contentAttributes: { type: Object, default: () => ({}) },
  contentType: {
    type: String,
    default: 'text',
    validator: value => Object.values(CONTENT_TYPES).includes(value),
  },
  conversationId: { type: Number, required: true },
  createdAt: { type: Number, required: true }, // eslint-disable-line vue/no-unused-properties
  currentUserId: { type: Number, required: true }, // eslint-disable-line vue/no-unused-properties
  groupWithNext: { type: Boolean, default: false },
  inboxId: { type: Number, default: null }, // eslint-disable-line vue/no-unused-properties
  inboxSupportsReplyTo: { type: Object, default: () => ({}) },
  inReplyTo: { type: Object, default: null }, // eslint-disable-line vue/no-unused-properties
  isEmailInbox: { type: Boolean, default: false },
  private: { type: Boolean, default: false },
  additionalAttributes: { type: Object, default: () => ({}) }, // eslint-disable-line vue/no-unused-properties
  sender: { type: Object, default: null },
  senderId: { type: Number, default: null },
  senderType: { type: String, default: null },
  sourceId: { type: String, default: '' }, // eslint-disable-line vue/no-unused-properties
  historyMetadata: { type: Object, default: () => ({}) }, // eslint-disable-line vue/no-unused-properties
  // Aceleres: quando essa mensagem eh o "wrapper" de um album (de imagens/
  // videos OU de stickers) agrupado em MessageList.vue, esse array traz
  // TODAS as mensagens do run (inclusive esta). ImageAlbumBubble e
  // StickerAlbumBubble consomem via useMessageContext().
  albumMessages: { type: Array, default: null }, // eslint-disable-line vue/no-unused-properties
});

const emit = defineEmits(['retry']);

const {
  isSelectionMode,
  selectedMessages,
  isMessageSelected,
  toggleMessageSelection,
  enterSelectionMode,
  exitSelectionMode,
} = useMessageSelection();
const isSelected = computed(() => isMessageSelected(props.id));
let suppressNextClick = false;
let suppressNextClickTimer = null;

function messageSelectionPayloadFor(message) {
  const source = message || {};

  return {
    id: source.id,
    content: source.content,
    attachments: source.attachments || [],
    messageType: source.messageType ?? source.message_type,
    status: source.status,
    contentAttributes:
      source.contentAttributes ?? source.content_attributes ?? {},
    conversationId: source.conversationId ?? source.conversation_id,
    createdAt: source.createdAt ?? source.created_at,
    sourceId: source.sourceId ?? source.source_id ?? '',
    private: source.private ?? false,
  };
}

function messageSelectionPayload() {
  return messageSelectionPayloadFor(props);
}

function handleMessageClick(e) {
  if (suppressNextClick) {
    suppressNextClick = false;
    if (suppressNextClickTimer) clearTimeout(suppressNextClickTimer);
    suppressNextClickTimer = null;
    e.preventDefault();
    e.stopPropagation();
    return;
  }
  if (!isSelectionMode.value || props.messageType === MESSAGE_TYPES.ACTIVITY)
    return;
  e.preventDefault();
  e.stopPropagation();
  toggleMessageSelection(messageSelectionPayload());
  if (
    selectedMessages.value.length === 0 &&
    window.matchMedia('(max-width: 767px)').matches
  ) {
    exitSelectionMode();
  }
}

const contextMenuPosition = ref({});
const showBackgroundHighlight = ref(false);
const showContextMenu = ref(false);
// Aceleres: reação rápida estilo WhatsApp — smile no hover + pill 6 emojis + '+'.
const showQuickReact = ref(false);
const initialShowPicker = ref(false);
const quickReactRef = ref(null);
// Aceleres: ref do menu de contexto pra reusar o handleForward na seta de forward.
const contextMenuRef = ref(null);
// Estilo inline (coords de viewport) da pill — ela é position:fixed teleported
// pro body, então left/top/bottom são relativos ao viewport, não ao bubble.
const pillStyle = ref({});
const pillDirection = ref('up');
let quickReactOutsideHandler = null;
let quickReactScrollHandler = null;
let quickReactKeyHandler = null;
let quickReactViewportHandler = null;
const { t } = useI18n();
const route = useRoute();
const inboxGetter = useMapGetter('inboxes/getInbox');
const conversationGetter = useMapGetter('getConversationById');
const store = useStore();
const currentAccountIdGetter = useMapGetter('getCurrentAccountId');
const inbox = computed(() => inboxGetter.value(props.inboxId) || {});
const { replaceInstallationName } = useBranding();

/**
 * Computes the message variant based on props
 * @type {import('vue').ComputedRef<'user'|'agent'|'activity'|'private'|'bot'|'template'>}
 */
const variant = computed(() => {
  if (props.private) return MESSAGE_VARIANTS.PRIVATE;

  if (props.isEmailInbox) {
    const emailInboxTypes = [MESSAGE_TYPES.INCOMING, MESSAGE_TYPES.OUTGOING];
    if (emailInboxTypes.includes(props.messageType)) {
      return MESSAGE_VARIANTS.EMAIL;
    }
  }

  if (props.contentType === CONTENT_TYPES.INCOMING_EMAIL) {
    return MESSAGE_VARIANTS.EMAIL;
  }

  if (props.status === MESSAGE_STATUS.FAILED) return MESSAGE_VARIANTS.ERROR;
  if (props.contentAttributes?.isUnsupported)
    return MESSAGE_VARIANTS.UNSUPPORTED;

  if (props.contentAttributes?.externalEcho) {
    return MESSAGE_VARIANTS.AGENT;
  }

  const isBot =
    props.sender?.type === SENDER_TYPES.AGENT_BOT ||
    props.senderType === SENDER_TYPES.AGENT_BOT ||
    (!props.sender && !props.additionalAttributes?.senderName);
  if (isBot && props.messageType === MESSAGE_TYPES.OUTGOING) {
    return MESSAGE_VARIANTS.BOT;
  }

  const variants = {
    [MESSAGE_TYPES.INCOMING]: MESSAGE_VARIANTS.USER,
    [MESSAGE_TYPES.ACTIVITY]: MESSAGE_VARIANTS.ACTIVITY,
    [MESSAGE_TYPES.OUTGOING]: MESSAGE_VARIANTS.AGENT,
    [MESSAGE_TYPES.TEMPLATE]: MESSAGE_VARIANTS.TEMPLATE,
  };

  return variants[props.messageType] || MESSAGE_VARIANTS.USER;
});

const isBotOrAgentMessage = computed(() => {
  if (props.messageType === MESSAGE_TYPES.ACTIVITY) {
    return false;
  }
  // if an outgoing message is still processing, then it's definitely a
  // message sent by the current user
  if (
    props.status === MESSAGE_STATUS.PROGRESS &&
    props.messageType === MESSAGE_TYPES.OUTGOING
  ) {
    return true;
  }
  const senderId = props.senderId ?? props.sender?.id;
  const senderType = props.sender?.type ?? props.senderType;

  if (!senderType || !senderId) {
    return true;
  }

  if (
    [SENDER_TYPES.AGENT_BOT, SENDER_TYPES.CAPTAIN_ASSISTANT].includes(
      senderType
    )
  ) {
    return true;
  }

  return senderType.toLowerCase() === SENDER_TYPES.USER.toLowerCase();
});

/**
 * Computes the message orientation based on sender type and message type
 * @returns {import('vue').ComputedRef<'left'|'right'|'center'>} The computed orientation
 */
const orientation = computed(() => {
  // Albums are rendered after the batch settles and use persisted message
  // data, whose sender metadata can differ from the temporary message.
  // Keep their direction tied to the logical message type instead.
  if (Array.isArray(props.albumMessages) && props.albumMessages.length >= 2) {
    return props.messageType === MESSAGE_TYPES.OUTGOING
      ? ORIENTATION.RIGHT
      : ORIENTATION.LEFT;
  }

  if (props.messageType === MESSAGE_TYPES.OUTGOING) {
    return ORIENTATION.RIGHT;
  }

  if (isBotOrAgentMessage.value) {
    return ORIENTATION.RIGHT;
  }

  if (props.messageType === MESSAGE_TYPES.ACTIVITY) return ORIENTATION.CENTER;

  return ORIENTATION.LEFT;
});

const flexOrientationClass = computed(() => {
  const map = {
    [ORIENTATION.LEFT]: 'justify-start',
    [ORIENTATION.RIGHT]: 'justify-end',
    [ORIENTATION.CENTER]: 'justify-center',
  };

  return map[orientation.value];
});

const gridClass = computed(() => {
  const map = {
    [ORIENTATION.LEFT]: 'grid grid-cols-1fr',
    [ORIENTATION.RIGHT]: 'grid grid-cols-[1fr_24px]',
  };

  return map[orientation.value];
});

const gridTemplate = computed(() => {
  const map = {
    [ORIENTATION.LEFT]: `
      "bubble"
      "reactions"
      "meta"
    `,
    [ORIENTATION.RIGHT]: `
      "bubble avatar"
      "reactions spacer"
      "meta spacer"
    `,
  };

  return map[orientation.value];
});

const shouldGroupWithNext = computed(() => {
  if (props.status === MESSAGE_STATUS.FAILED) return false;

  return props.groupWithNext;
});

const shouldShowAvatar = computed(() => {
  if (props.messageType === MESSAGE_TYPES.ACTIVITY) return false;
  if (orientation.value === ORIENTATION.LEFT) return false;

  return true;
});

const isPossibleAbsenceAlertMessage = computed(
  () =>
    props.contentAttributes?.aceleresComponent === 'possible_absence_alert' ||
    props.contentAttributes?.aceleres_component === 'possible_absence_alert'
);

const componentToRender = computed(() => {
  // Aceleres: bolha de possivel falta pausada temporariamente.
  // if (isPossibleAbsenceAlertMessage.value) return AceleresPossibleAbsenceAlert;

  // Aceleres: album de imagens/videos ou de stickers tipo WhatsApp — quando
  // MessageList detectou um run de 2+ mensagens consecutivas do mesmo sender
  // e passou o array via `album-messages`, ignoramos os checks abaixo e
  // renderizamos o grid. MessageList só forma runs homogêneos (nunca mistura
  // sticker com foto), então checar a primeira mensagem do run basta.
  if (Array.isArray(props.albumMessages) && props.albumMessages.length >= 2) {
    const firstAlbumAttrs = props.albumMessages[0]?.contentAttributes || {};
    const firstAlbumMediaType = String(
      firstAlbumAttrs.wameMediaType ?? firstAlbumAttrs.wame_media_type ?? ''
    ).toLowerCase();
    return firstAlbumMediaType === 'sticker'
      ? StickerAlbumBubble
      : ImageAlbumBubble;
  }

  if (props.isEmailInbox && !props.private) {
    const emailInboxTypes = [MESSAGE_TYPES.INCOMING, MESSAGE_TYPES.OUTGOING];
    if (emailInboxTypes.includes(props.messageType)) return EmailBubble;
  }

  if (props.contentType === CONTENT_TYPES.INPUT_CSAT) {
    return CSATBubble;
  }

  if (
    [CONTENT_TYPES.INPUT_SELECT, CONTENT_TYPES.FORM].includes(props.contentType)
  ) {
    return FormBubble;
  }

  if (props.contentType === CONTENT_TYPES.VOICE_CALL) {
    return VoiceCallBubble;
  }

  if (props.contentType === CONTENT_TYPES.INCOMING_EMAIL) {
    return EmailBubble;
  }

  if (props.contentAttributes?.isUnsupported) {
    return UnsupportedBubble;
  }

  if (props.contentAttributes.type === 'dyte') {
    return DyteBubble;
  }

  const instagramSharedTypes = [
    ATTACHMENT_TYPES.STORY_MENTION,
    ATTACHMENT_TYPES.IG_STORY,
    ATTACHMENT_TYPES.IG_STORY_REPLY,
    ATTACHMENT_TYPES.IG_POST,
  ];
  if (instagramSharedTypes.includes(props.contentAttributes.imageType)) {
    return InstagramStoryBubble;
  }

  if (Array.isArray(props.attachments) && props.attachments.length === 1) {
    const fileType = props.attachments[0].fileType;
    const wameMediaType =
      props.contentAttributes?.wameMediaType ??
      props.contentAttributes?.wame_media_type;
    const normalizedWameMediaType = String(wameMediaType || '').toLowerCase();
    const isWameDocument = normalizedWameMediaType === 'document';
    const isWameGif = normalizedWameMediaType === 'gif';
    const isWameSticker =
      props.contentType === CONTENT_TYPES.STICKER ||
      normalizedWameMediaType === 'sticker';
    const hasOnlyTechnicalMediaContent = [
      'mídia recebida',
      'media received',
    ].includes(
      String(props.content || '')
        .trim()
        .toLowerCase()
    );

    // A API do Chatwoot converte audio/* em attachment de áudio. A WAME
    // preserva a intenção original em wame_media_type; ela prevalece para que
    // um FLAC/MP3 enviado como documento não vire uma gravação na interface.
    // Documents keep their document card even when WhatsApp includes a caption.
    // The caption is rendered inside FileBubble below the file metadata.
    if (isWameDocument || fileType === ATTACHMENT_TYPES.FILE) return FileBubble;
    // GIFs diretos são image/gif. Os GIFs nativos do WhatsApp podem chegar à
    // WAME como MP4 silencioso: preservamos a semântica de GIF, mas precisamos
    // do VideoBubble para o navegador reproduzir o arquivo, sem controles.
    if (isWameGif && fileType === ATTACHMENT_TYPES.VIDEO) return VideoBubble;
    if (isWameGif) return ImageBubble;
    if (isWameSticker) return ImageBubble;
    if (fileType === ATTACHMENT_TYPES.IMAGE) return ImageBubble;
    // Vídeos com legenda continuam no VideoBubble para que a legenda tenha
    // o limite e a expansão controlados pela própria mídia.
    if (fileType === ATTACHMENT_TYPES.VIDEO) return VideoBubble;
    if (fileType === ATTACHMENT_TYPES.IG_REEL) return VideoBubble;
    if (!props.content || hasOnlyTechnicalMediaContent) {
      if (fileType === ATTACHMENT_TYPES.AUDIO) return AudioBubble;
      if (fileType === ATTACHMENT_TYPES.EMBED) return EmbedBubble;
      if (fileType === ATTACHMENT_TYPES.LOCATION) return LocationBubble;
    }
    // Attachment content is the name of the contact
    if (fileType === ATTACHMENT_TYPES.CONTACT) return ContactBubble;
  }

  return TextBubble;
});

const shouldShowContextMenu = computed(() => {
  return !props.contentAttributes?.isUnsupported;
});

const isBubble = computed(() => {
  return props.messageType !== MESSAGE_TYPES.ACTIVITY;
});
const isTextOnlyMessage = computed(
  () => componentToRender.value === TextBubble && !props.attachments?.length
);

const isImageBubble = computed(() => componentToRender.value === ImageBubble);
// Aceleres: "álbum" aqui significa "essa bolha representa 2+ mensagens
// agrupadas visualmente" — vale tanto pro grid de fotos/vídeos quanto pro
// par de stickers. Reaproveitado por copy/replyTo/addToNote (não fazem
// sentido apontando pra várias mensagens de uma vez) e pela seleção múltipla
// abaixo, sem duplicar esse conjunto de regras pros dois tipos de álbum.
const isAlbumBubble = computed(() =>
  [ImageAlbumBubble, StickerAlbumBubble].includes(componentToRender.value)
);
const isStickerAlbumBubble = computed(
  () => componentToRender.value === StickerAlbumBubble
);
const isStickerBubble = computed(() => {
  const attributes = props.contentAttributes || {};
  const mediaType =
    attributes.wameMediaType ?? attributes.wame_media_type ?? '';

  return (
    isImageBubble.value &&
    (props.contentType === CONTENT_TYPES.STICKER ||
      String(mediaType).toLowerCase() === 'sticker')
  );
});
// Aceleres: StickerAlbumBubble fica de fora — ela tem sua PRÓPRIA flechinha
// por sticker (ver StickerAlbum.vue), então a flechinha genérica única do
// Message.vue é suprimida pra ela (ver `!isStickerAlbumBubble` no botão
// abaixo). Mantê-la aqui seria estado morto, sem efeito nenhum.
const isMediaChevronBubble = computed(() =>
  [ImageBubble, ImageAlbumBubble, FileBubble, VideoBubble].includes(
    componentToRender.value
  )
);
const hasAdaptiveMediaMenuSlot = computed(
  () => isImageBubble.value || componentToRender.value === VideoBubble
);
const chevronStyleClasses = computed(() => {
  if (isStickerBubble.value) {
    return [
      '!top-1.5',
      '!right-1.5',
      '!h-7',
      '!w-7',
      '!rounded-full',
      '!bg-n-slate-3',
      '!text-n-slate-11',
      'shadow-sm',
    ];
  }

  if (isMediaChevronBubble.value) {
    return ['!top-2', '!right-2', '!bg-transparent', '!text-n-slate-11'];
  }

  return ['!top-1.5', '!right-2', '!bg-transparent', '!text-n-slate-11'];
});
const adaptiveChevronToneClasses = tone => {
  if (isStickerBubble.value) return [];

  return tone === 'dark'
    ? ['!text-white', 'drop-shadow-md']
    : ['!text-n-slate-11', 'drop-shadow-sm'];
};
const isOutgoingImageMessage = computed(
  () => isImageBubble.value && props.messageType === MESSAGE_TYPES.OUTGOING
);

const isMessageDeleted = computed(() => {
  return props.contentAttributes?.deleted;
});

const isWameMediaPending = computed(() => {
  const attrs = props.contentAttributes || {};
  const stage = String(
    attrs.wameMediaStage ?? attrs.wame_media_stage ?? ''
  ).toLowerCase();

  return (
    (attrs.wameMediaPending ?? attrs.wame_media_pending) === true ||
    ['bank', 'chatwoot'].includes(stage)
  );
});

const isEvolutionWhatsappMessage = computed(() => {
  const channelType = inbox.value?.channel_type;
  const sourceId = props.sourceId || '';
  return (
    channelType === 'Channel::Api' &&
    typeof sourceId === 'string' &&
    sourceId.length > 0
  );
});

const isWameInbox = computed(() => {
  const attrs =
    inbox.value?.additional_attributes ||
    inbox.value?.channel?.additional_attributes ||
    {};
  return (
    inbox.value?.channel_type === 'Channel::Api' &&
    attrs.aceleres_outbound_mode === 'webhook' &&
    Boolean(attrs.aceleres_instance_id)
  );
});

const isWithinEditWindow = computed(() => {
  if (!props.createdAt) return false;
  // props.createdAt vem em segundos (unix). Janela nativa do WhatsApp: 15 min.
  const nowSec = Math.floor(Date.now() / 1000);
  return nowSec - Number(props.createdAt) < 15 * 60;
});

const isMessageEdited = computed(() => {
  return !!props.contentAttributes?.edited;
});

const payloadForContextMenu = computed(() => {
  return {
    id: props.id,
    content_attributes: props.contentAttributes,
    content: props.content,
    conversation_id: props.conversationId,
    attachments: props.attachments,
    status: props.status,
    created_at: props.createdAt,
    source_id: props.sourceId,
    message_type: props.messageType,
  };
});

const contextMenuSelectionMessages = computed(() => {
  if (!isAlbumBubble.value || !Array.isArray(props.albumMessages)) return [];

  return props.albumMessages.map(messageSelectionPayloadFor);
});

const contextMenuEnabledOptions = computed(() => {
  const hasText = !!props.content;
  const hasAttachments = !!(props.attachments && props.attachments.length > 0);
  const firstAttachmentType = hasAttachments
    ? props.attachments[0].fileType
    : null;
  const isAudio = firstAttachmentType === 'audio';
  const isImage = firstAttachmentType === 'image';

  const isOutgoing = props.messageType === MESSAGE_TYPES.OUTGOING;
  const isFailedMessage = props.status === MESSAGE_STATUS.FAILED;
  const isFailedOrProcessing =
    isFailedMessage || props.status === MESSAGE_STATUS.PROGRESS;

  return {
    // Aceleres: itens alinhados ao popup do WhatsApp.
    info: !isFailedOrProcessing && !isMessageDeleted.value,
    react: !isFailedOrProcessing && !isMessageDeleted.value,
    download:
      hasAttachments &&
      !isFailedOrProcessing &&
      !isMessageDeleted.value &&
      !isStickerBubble.value &&
      !isStickerAlbumBubble.value,
    attachmentType: firstAttachmentType,
    copy: hasText && !isAudio && !isAlbumBubble.value,
    // Aceleres: "Editar mensagem" so aparece no fluxo suportado pelo Zoraxy:
    // mensagem de texto, sem anexos, enviada via WhatsApp/Evolution e dentro
    // da janela de 15 min. O backend ainda valida tudo novamente.
    edit: (() => {
      const common = {
        isOutgoing,
        hasText,
        notDeleted: !isMessageDeleted.value,
        noAttachments: !hasAttachments,
      };
      const sentCond = {
        ...common,
        isEvolutionWhatsappMessage:
          isEvolutionWhatsappMessage.value && !isWameInbox.value,
        notFailedOrProcessing: !isFailedOrProcessing,
        isWithinEditWindow: isWithinEditWindow.value,
      };
      const failedCond = {
        ...common,
        isApiInbox: inbox.value?.channel_type === 'Channel::Api',
        isFailedMessage,
      };
      const ok =
        Object.values(sentCond).every(Boolean) ||
        Object.values(failedCond).every(Boolean);
      if (!ok && typeof window !== 'undefined') {
        // eslint-disable-next-line no-console
        console.debug('[aceleres-edit-blocked]', props.id, {
          sentCond,
          failedCond,
          channelType: inbox.value?.channel_type,
          sourceId: props.sourceId,
          createdAt: props.createdAt,
          ageSec: Math.floor(Date.now() / 1000) - Number(props.createdAt || 0),
        });
      }
      return ok;
    })(),
    delete: false,
    cannedResponse: isOutgoing && hasText && !isMessageDeleted.value,
    copyLink: !isFailedOrProcessing,
    translate: !isFailedOrProcessing && !isMessageDeleted.value && hasText,
    replyTo:
      !isAlbumBubble.value &&
      !props.private &&
      props.inboxSupportsReplyTo.outgoing &&
      !isFailedOrProcessing,
    forward: !isFailedOrProcessing && !isMessageDeleted.value,
    star: !isFailedOrProcessing && !isMessageDeleted.value,
    addToNote:
      !isAlbumBubble.value &&
      hasText &&
      !isAudio &&
      !isImage &&
      !isMessageDeleted.value,
    pinned:
      conversationGetter.value(props.conversationId)?.additional_attributes
        ?.pinned_message_id === props.id,
  };
});

const shouldRenderMessage = computed(() => {
  // Aceleres: esconde mensagens ja criadas do alerta de possivel falta enquanto
  // a funcionalidade esta pausada para o publico.
  if (isPossibleAbsenceAlertMessage.value) return false;

  const hasAttachments = !!(props.attachments && props.attachments.length > 0);
  const isEmailContentType = props.contentType === CONTENT_TYPES.INCOMING_EMAIL;
  const isUnsupported = props.contentAttributes?.isUnsupported;
  const isAnIntegrationMessage =
    props.contentType === CONTENT_TYPES.INTEGRATIONS;
  const isFailedMessage = props.status === MESSAGE_STATUS.FAILED;
  const hasExternalError = !!props.contentAttributes?.externalError;

  return (
    hasAttachments ||
    props.content ||
    isEmailContentType ||
    isUnsupported ||
    isAnIntegrationMessage ||
    isFailedMessage ||
    hasExternalError
  );
});

// Aceleres: ancora a pilha de ações e reações na própria bolha, não no ponto
// do clique. Assim as duas partes permanecem alinhadas em mensagens curtas,
// longas, recebidas e enviadas.
// (findBubble, findContextMenuTrigger, getDashboardZoom, getVisualViewportSize,
// fixedPosition e computeMenuAnchor vêm de contextMenuAnchor.js — StickerAlbum.vue
// usa as mesmas pro menu de cada sticker do par.)

function getQuickReactViewportSize() {
  const zoom = getDashboardZoom();
  const rootRect = document.documentElement.getBoundingClientRect();

  return {
    zoom,
    width: rootRect.width || window.innerWidth * zoom,
    height: rootRect.height || window.innerHeight * zoom,
  };
}

function announceMessageInteraction(interaction) {
  emitter.emit(BUS_EVENTS.MESSAGE_INTERACTION_OPENED, {
    messageId: props.id,
    interaction,
  });
}

function openContextMenu(e, explicitAnchor = null) {
  // Aceleres: em click direito (e long-press) abrimos o popup mesmo em <img>
  // e <a> — usuário pediu pra que reaja em imagens também. Só pulamos quando
  // a UI explicitamente marca skip-context-menu (botões internos da bolha).
  const shouldSkipContextMenu =
    isSelectionMode.value ||
    e.target?.classList.contains('skip-context-menu') ||
    !!getSelection().toString();
  if (shouldSkipContextMenu) return;
  e.preventDefault();
  if (e.type === 'contextmenu') {
    useTrack(ACCOUNT_EVENTS.OPEN_MESSAGE_CONTEXT_MENU);
  }
  announceMessageInteraction('context-menu');
  contextMenuPosition.value = computeMenuAnchor(e, {
    anchor: explicitAnchor || findContextMenuTrigger(e),
  });
  showContextMenu.value = true;
}

function handleReaction(emoji) {
  store.dispatch('reactToMessage', {
    accountId: currentAccountIdGetter.value,
    messageId: props.id,
    emoji,
  });
}

// Aceleres: modal de detalhe das reacoes (quem reagiu, trocar e remover a
// propria). Abre ancorado na chip de reacao, como no WhatsApp. O card NAO
// fecha ao reagir: a chip da propria reacao alterna entre 1 e 0 ali mesmo.
const showReactionsModal = ref(false);
// Guarda o ELEMENTO da pilula, não um retângulo copiado: o card mede a
// âncora quando vai desenhar e sempre que a página rola, em vez de ficar
// preso à posição do instante do clique. shallowRef porque é um nó do DOM —
// não há nada aqui que valha a pena tornar reativo em profundidade.
const reactionsAnchorEl = shallowRef(null);
function openReactionsModal(el) {
  reactionsAnchorEl.value = el ?? null;
  showReactionsModal.value = true;
}
function closeContextMenu() {
  if (!showContextMenu.value && !initialShowPicker.value) return;
  const shouldCloseQuickReact = initialShowPicker.value;
  showContextMenu.value = false;
  contextMenuPosition.value = { x: null, y: null };
  initialShowPicker.value = false;
  if (shouldCloseQuickReact) closeQuickReact();
}

// O atalho ao lado da bolha abre diretamente o seletor de destinos. Para um
// álbum, o ContextMenu recebe todas as mensagens e o encaminhamento mantém o
// grupo inteiro, como no WhatsApp.
function forwardThisMessage() {
  contextMenuRef.value?.openForwardModal?.();
}

// Aceleres: pill de reação rápida (WhatsApp-like). A pill é renderizada via
// <Teleport to="body"> + position:fixed — MESMO padrão do emoji-picker
// (MessageContextMenu). Motivo: a pill abre PARA CIMA nos outgoing (is-right)
// e era clipada pelo overflow-y-auto do .conversation-panel quando ultrapassava
// a borda superior do container de scroll, ficando "metade cortada". Sendo
// fixed no body, ela escapa de qualquer overflow/stacking e fica por cima de
// tudo. Coords calculadas a partir do rect do botão smile; fecha no scroll
// (a pill é fixed, não acompanha a mensagem ao rolar).
function closeQuickReact() {
  // O picker completo mantém a barra rápida como sua âncora visual. Nenhum
  // handler secundário pode desmontá-la enquanto ele estiver aberto.
  if (initialShowPicker.value) return;

  if (
    !showQuickReact.value &&
    !quickReactOutsideHandler &&
    !quickReactScrollHandler &&
    !quickReactKeyHandler &&
    !quickReactViewportHandler
  ) {
    return;
  }
  showQuickReact.value = false;
  pillStyle.value = {};
  pillDirection.value = 'up';
  if (quickReactOutsideHandler) {
    document.removeEventListener('pointerdown', quickReactOutsideHandler, true);
    quickReactOutsideHandler = null;
  }
  if (quickReactScrollHandler) {
    document.removeEventListener('scroll', quickReactScrollHandler, true);
    document.removeEventListener('touchmove', quickReactScrollHandler, true);
    quickReactScrollHandler = null;
  }
  if (quickReactKeyHandler) {
    document.removeEventListener('keydown', quickReactKeyHandler);
    quickReactKeyHandler = null;
  }
  if (quickReactViewportHandler) {
    window.removeEventListener('resize', quickReactViewportHandler);
    window.visualViewport?.removeEventListener(
      'resize',
      quickReactViewportHandler
    );
    window.visualViewport?.removeEventListener(
      'scroll',
      quickReactViewportHandler
    );
    quickReactViewportHandler = null;
  }
}

function handleMessageInteractionOpened({ messageId, interaction } = {}) {
  const isCurrentMessage = Number(messageId) === Number(props.id);
  const isQuickPicker = interaction === 'quick-reaction-picker';
  if (
    !isCurrentMessage ||
    (interaction !== 'context-menu' && !isQuickPicker)
  ) {
    closeContextMenu();
  }
  if (
    !isCurrentMessage ||
    (interaction !== 'quick-reaction' && !isQuickPicker)
  ) {
    closeQuickReact();
  }
}
// Aceleres: posiciona a pill junto ao botão smile e evita cobrir mensagens
// somente de texto quando a pill não couber acima da bolha.
function positionQuickReactPill(smile) {
  const root = quickReactRef.value;
  if (!root || !smile) return;

  const rect = smile.getBoundingClientRect();
  const {
    zoom: pageZoom,
    width: viewportWidth,
    height: viewportHeight,
  } = getQuickReactViewportSize();
  const pillRect = root.getBoundingClientRect();
  const pillW = pillRect.width || (root.offsetWidth || 280) * pageZoom;
  const pillH = pillRect.height || (root.offsetHeight || 48) * pageZoom;
  const GAP = 4;
  const PAD = 8;
  const anchorTop = rect.top;
  const anchorCx = rect.left + rect.width / 2;
  const visualTop = Math.min(
    Math.max(anchorTop - pillH - GAP, PAD),
    Math.max(PAD, viewportHeight - pillH - PAD)
  );
  let adjustedTop = visualTop;
  let opensAbove = true;

  if (isTextOnlyMessage.value) {
    const bubble = smile
      .closest?.('.aceleres-bubble-with-actions')
      ?.querySelector?.('.aceleres-bubble-surface');
    const bubbleRect = bubble?.getBoundingClientRect?.();

    if (bubbleRect && visualTop + pillH > bubbleRect.top) {
      const fitsAbove = bubbleRect.top >= pillH + GAP + PAD;
      const fitsBelow =
        viewportHeight - bubbleRect.bottom >= pillH + GAP + PAD;

      if (fitsAbove) {
        adjustedTop = bubbleRect.top - pillH - GAP;
      } else if (fitsBelow) {
        adjustedTop = bubbleRect.bottom + GAP;
        opensAbove = false;
      } else if (bubbleRect.top >= viewportHeight - bubbleRect.bottom) {
        adjustedTop = Math.max(bubbleRect.top - pillH - GAP, PAD);
      } else {
        adjustedTop = Math.min(
          bubbleRect.bottom + GAP,
          viewportHeight - pillH - PAD
        );
        opensAbove = false;
      }
    }
  }

  const visualCenterX = Math.min(
    Math.max(anchorCx, PAD + pillW / 2),
    viewportWidth - PAD - pillW / 2
  );

  pillStyle.value = {
    left: fixedPosition(visualCenterX, pageZoom),
    top: fixedPosition(adjustedTop, pageZoom),
    bottom: '',
  };
  pillDirection.value = opensAbove ? 'up' : 'down';
}

function installQuickReactListeners() {
  // O listener entra no próximo ciclo para não capturar o toque/click de
  // abertura da própria faixa de reações.
  setTimeout(() => {
    if (!showQuickReact.value) return;
    quickReactOutsideHandler = event => {
      const root = quickReactRef.value;
      // Enquanto o picker completo estiver aberto, ele próprio controla o
      // click-outside. Assim, as abas de categoria não fecham a barra rápida.
      if (initialShowPicker.value) return;
      if (root && !root.contains(event.target)) {
        closeQuickReact();
      }
    };
    document.addEventListener('pointerdown', quickReactOutsideHandler, true);
  }, 0);

  quickReactScrollHandler = () => closeQuickReact();
  document.addEventListener('scroll', quickReactScrollHandler, true);
  document.addEventListener('touchmove', quickReactScrollHandler, {
    capture: true,
    passive: true,
  });
  quickReactKeyHandler = event => {
    if (event.key === 'Escape') closeQuickReact();
  };
  document.addEventListener('keydown', quickReactKeyHandler);
  quickReactViewportHandler = () => closeQuickReact();
  window.addEventListener('resize', quickReactViewportHandler, {
    passive: true,
  });
  window.visualViewport?.addEventListener('resize', quickReactViewportHandler, {
    passive: true,
  });
  window.visualViewport?.addEventListener('scroll', quickReactViewportHandler, {
    passive: true,
  });
}

function openQuickReactAtAnchor(anchor) {
  announceMessageInteraction('quick-reaction');
  showQuickReact.value = true;
  nextTick(() => positionQuickReactPill(anchor));
  installQuickReactListeners();
}

function toggleQuickReact(e) {
  e?.stopPropagation?.();
  if (showQuickReact.value) {
    closeQuickReact();
    return;
  }
  // Âncora no botão smile que disparou o clique. Robusto: sobe até o
  // message-bubble-container e querySelector o smile de lá (currentTarget
  // pode estar resetado fora do dispatch).
  const triggerBtn = e?.currentTarget || e?.target;
  const container =
    triggerBtn?.closest?.('.message-bubble-container') ||
    e?.target?.closest?.('.message-bubble-container');
  const smile =
    container?.querySelector?.('.aceleres-bubble-smile') || triggerBtn;
  openQuickReactAtAnchor(smile);
}
function handleQuickReact(emoji) {
  handleReaction(emoji);
  closeQuickReactAndPicker();
  if (
    isSelectionMode.value &&
    window.matchMedia('(max-width: 767px)').matches
  ) {
    exitSelectionMode();
  }
}
function closeQuickReactAndPicker() {
  if (initialShowPicker.value) {
    closeContextMenu();
    return;
  }
  closeQuickReact();
}
function openFullPickerFromPill(e) {
  announceMessageInteraction('quick-reaction-picker');
  initialShowPicker.value = true;
  if (
    isSelectionMode.value &&
    window.matchMedia('(max-width: 767px)').matches
  ) {
    contextMenuPosition.value = {
      right: '8px',
      bottom: '8px',
      left: '8px',
      top: 'auto',
      direction: 'up',
      transformOrigin: 'bottom center',
    };
    showContextMenu.value = true;
    return;
  }
  const quickPill = quickReactRef.value;
  if (quickPill) {
    contextMenuPosition.value = computePickerAnchorFromRect(
      quickPill.getBoundingClientRect()
    );
    showContextMenu.value = true;
    return;
  }

  // Fallback para o botão smile quando a barra de reações não estiver
  // disponível no momento do clique.
  const container =
    e?.target?.closest?.('.message-bubble-container') ||
    e?.currentTarget?.closest?.('.message-bubble-container');
  const smile = container?.querySelector?.('.aceleres-bubble-smile');
  if (smile) {
    contextMenuPosition.value = computePickerAnchorFromRect(
      smile.getBoundingClientRect()
    );
    showContextMenu.value = true;
    return;
  }
  openContextMenu(e);
}

// Aceleres: posiciona o emoji-picker-card em relação ao elemento que abriu o
// seletor, mantendo-o centralizado e dentro da área visível da conversa.
function computePickerAnchorFromRect(rect) {
  const PICKER_W = 420;
  const PICKER_H = 280;
  const OFFSET = 16;
  const PAD = 8;

  const messagesArea = document.querySelector(
    '.flex.flex-col.justify-between.flex-grow.h-full.min-w-0.m-0.scrollbar-thin'
  );
  const areaRect = messagesArea ? messagesArea.getBoundingClientRect() : null;
  const areaTop = areaRect ? areaRect.top : 0;
  const areaBottom = areaRect ? areaRect.bottom : window.innerHeight;

  const smileTop = rect.top;
  const smileBottom = rect.bottom;
  const smileCenterX = rect.left + rect.width / 2;
  const spaceAbove = smileTop - areaTop;
  const spaceBelow = areaBottom - smileBottom;
  const needed = PICKER_H + OFFSET + PAD;

  let topPx = '';
  let bottomPx = '';
  let direction;
  if (spaceAbove >= needed) {
    direction = 'up';
    bottomPx = window.innerHeight - smileTop + OFFSET + 'px';
  } else if (spaceBelow >= needed) {
    direction = 'down';
    topPx = smileBottom + OFFSET + 'px';
  } else if (spaceBelow >= spaceAbove) {
    direction = 'down';
    topPx = smileBottom + OFFSET + 'px';
  } else {
    direction = 'up';
    bottomPx = window.innerHeight - smileTop + OFFSET + 'px';
  }

  let leftEdge = smileCenterX - PICKER_W / 2;
  if (leftEdge < PAD) leftEdge = PAD;
  const maxLeft = window.innerWidth - PAD - PICKER_W;
  if (leftEdge > maxLeft) leftEdge = Math.max(PAD, maxLeft);

  const originY = direction === 'down' ? 'top' : 'bottom';
  return {
    left: leftEdge + 'px',
    right: '',
    top: topPx,
    bottom: bottomPx,
    direction,
    transformOrigin: originY + ' center',
    anchorLeft: rect.left,
    anchorTop: rect.top,
    anchorRight: rect.right,
    anchorBottom: rect.bottom,
    anchorWidth: rect.width,
    anchorHeight: rect.height,
    anchorCenter: smileCenterX,
  };
}

let longPressTimer = null;
let touchStartPoint = null;
let longPressTriggered = false;
const LONG_PRESS_DELAY = 450;
const LONG_PRESS_MOVE_TOLERANCE = 12;

function cancelLongPress() {
  if (longPressTimer) clearTimeout(longPressTimer);
  longPressTimer = null;
  touchStartPoint = null;
}

function onTouchStart(e) {
  if (isSelectionMode.value && selectedMessages.value.length > 0) return;
  cancelLongPress();
  longPressTriggered = false;
  const touch = e.touches?.[0];
  if (!touch) return;

  touchStartPoint = {
    clientX: touch.clientX,
    clientY: touch.clientY,
    target: touch.target || e.target,
    currentTarget: e.currentTarget,
  };
  longPressTimer = setTimeout(() => {
    const point = touchStartPoint;
    if (!point) return;

    if (window.matchMedia('(max-width: 767px)').matches) {
      const row = point.currentTarget;
      const messageContainer = row?.closest?.('.message-bubble-container');
      const anchor = messageContainer || row;

      closeContextMenu();
      getSelection()?.removeAllRanges();
      enterSelectionMode();
      if (!isMessageSelected(props.id)) {
        toggleMessageSelection(messageSelectionPayload());
      }
      suppressNextClick = true;
      longPressTriggered = true;

      if (contextMenuEnabledOptions.value.react && anchor) {
        openQuickReactAtAnchor(anchor);
      }
    } else {
      announceMessageInteraction('context-menu');
      contextMenuPosition.value = computeMenuAnchor({
        target: point.target,
        currentTarget: point.currentTarget,
        clientX: point.clientX,
        clientY: point.clientY,
      });
      showContextMenu.value = true;
    }

    longPressTimer = null;
    window.navigator?.vibrate?.(8);
  }, LONG_PRESS_DELAY);
}

function onTouchMove(e) {
  if (!longPressTimer || !touchStartPoint) return;
  const touch = e.touches?.[0];
  if (!touch) return;
  const deltaX = touch.clientX - touchStartPoint.clientX;
  const deltaY = touch.clientY - touchStartPoint.clientY;
  if (Math.hypot(deltaX, deltaY) > LONG_PRESS_MOVE_TOLERANCE) {
    cancelLongPress();
  }
}

function onTouchEnd() {
  cancelLongPress();
  if (!longPressTriggered) return;
  longPressTriggered = false;
  if (suppressNextClickTimer) clearTimeout(suppressNextClickTimer);
  suppressNextClickTimer = setTimeout(() => {
    suppressNextClick = false;
    suppressNextClickTimer = null;
  }, 700);
}

function handleReplyTo() {
  const replyStorageKey = LOCAL_STORAGE_KEYS.MESSAGE_REPLY_TO;
  const { conversationId, id: replyTo } = props;

  LocalStorage.updateJsonStore(replyStorageKey, conversationId, replyTo);
  emitter.emit(BUS_EVENTS.TOGGLE_REPLY_TO_MESSAGE, props);
}

const avatarInfo = computed(() => {
  // Aceleres: mensagens da Aurora (IA) exibem "IA" no lugar do nome do
  // usuario-robo (admin real cujo token a Aurora usa para postar). Baseado
  // na flag content_attributes.aurora_ai -> robusto e cobre todas as msgs.
  if (props.contentAttributes?.auroraAi ?? props.contentAttributes?.aurora_ai) {
    return { name: 'IA', src: '' };
  }
  if (props.contentAttributes?.externalEcho) {
    const { name, avatar_url, channel_type, medium } = inbox.value;
    const iconName = avatar_url
      ? null
      : getInboxIconByType(channel_type, medium);
    return {
      name: iconName ? '' : name || t('CONVERSATION.NATIVE_APP'),
      src: avatar_url || '',
      iconName,
    };
  }

  // If no sender, check for Slack (or other integration) sender info
  if (!props.sender) {
    const { senderName, senderAvatarUrl } = props.additionalAttributes || {};
    if (senderName) {
      return { name: senderName, src: senderAvatarUrl ?? '' };
    }
    return { name: t('CONVERSATION.BOT'), src: '' };
  }

  const { sender } = props;
  const { name, type, avatarUrl, thumbnail, additionalAttributes } =
    sender || {};
  const customName = additionalAttributes?.aceleresContactName;

  // If sender type is agent bot, use avatarUrl
  if ([SENDER_TYPES.AGENT_BOT, SENDER_TYPES.CAPTAIN_ASSISTANT].includes(type)) {
    return {
      name: name ?? '',
      src: avatarUrl ?? '',
    };
  }

  // Aceleres: Channel::Api costumava exibir a Integracao Aceleres como sender
  // em toda mensagem sincronizada via WAME, e o band-aid aqui trocava o nome
  // exibido pelo assignee ATUAL da conversa — o que reescrevia
  // retroativamente a autoria de mensagens antigas a cada transferencia de
  // responsavel. Agora que o backend resolve o autor real (sender_id) via
  // content_attributes.wame_author_chatwoot_user_id (ver
  // Messages::MessageBuilder#wame_author_sender), so cai no rotulo neutro
  // quando esse autor nao foi/nao pode ser identificado — nunca no assignee.
  // type vem da API como "user" (lowercase) enquanto SENDER_TYPES.USER === "User";
  // o proprio arquivo ja faz toLowerCase() em outro check (isUser computed).
  //
  // A DECISAO de confiar ou nao no sender mora em isUnattributedWameMessage,
  // compartilhada com o balao (Base.vue) e com o album de stickers. Ela estava
  // copiada em cada um deles, e a copia do album divergiu: lia o sender cru e
  // trocava o rotulo do par de figurinhas do nome da instancia para o dono do
  // token admin no instante em que as mensagens eram agrupadas. A
  // APRESENTACAO continua sendo de cada componente — aqui embaixo ela devolve
  // avatar, suprime nome generico de inbox e cai em "Atendente".
  if (
    inbox.value.channel_type === 'Channel::Api' &&
    type?.toLowerCase?.() === SENDER_TYPES.USER.toLowerCase()
  ) {
    if (
      !isUnattributedWameMessage({
        contentAttributes: props.contentAttributes,
        senderType: type,
        inbox: inbox.value,
      })
    ) {
      return { name: name ?? '', src: thumbnail ?? '' };
    }
    // Sem autor identificavel (mensagem WAME antiga, sincronizada antes da
    // propagacao do autor real): cai pro nome do inbox, que deve ser o nome
    // do vendedor dono da instancia. Inboxes provisionados pela Evolution
    // nascem com nome GENERICO ("WhatsApp · hash"); nesse caso NUNCA exibir
    // "WhatsApp" como remetente — usa rotulo neutro "Atendente". Os inboxes
    // existentes foram renomeados para o nome do vendedor (membro unico do
    // inbox).
    const inboxName = (inbox.value.name || '').split('·')[0].trim();
    const isGenericChannelName = /^whats\s*app/i.test(inboxName);
    if (inboxName && !isGenericChannelName) {
      return { name: inboxName, src: '' };
    }
    return { name: 'Atendente', src: '' };
  }

  // For all other senders, use thumbnail
  return {
    name: customName || name || '',
    src: thumbnail ?? '',
  };
});

const avatarTooltip = computed(() => {
  if (props.contentAttributes?.externalEcho) {
    return replaceInstallationName(t('CONVERSATION.NATIVE_APP_ADVISORY'));
  }
  if (avatarInfo.value.name === '') return '';
  return `${t('CONVERSATION.SENT_BY')} ${avatarInfo.value.name}`;
});

const setupHighlightTimer = () => {
  if (Number(route.query.messageId) !== Number(props.id)) {
    return;
  }

  showBackgroundHighlight.value = true;
  const HIGHLIGHT_TIMER = 1000;
  useTimeoutFn(() => {
    showBackgroundHighlight.value = false;
  }, HIGHLIGHT_TIMER);
};

function handleOpenMessageInfo({ messageId } = {}) {
  if (Number(messageId) !== Number(props.id)) return;
  if (!contextMenuEnabledOptions.value.info) return;
  contextMenuRef.value?.handleMessageInfo?.();
}

onMounted(() => {
  setupHighlightTimer();
  emitter.on(
    BUS_EVENTS.MESSAGE_INTERACTION_OPENED,
    handleMessageInteractionOpened
  );
  emitter.on(BUS_EVENTS.OPEN_MESSAGE_INFO, handleOpenMessageInfo);
});

onBeforeUnmount(() => {
  cancelLongPress();
  if (suppressNextClickTimer) clearTimeout(suppressNextClickTimer);
  closeQuickReact();
  emitter.off(
    BUS_EVENTS.MESSAGE_INTERACTION_OPENED,
    handleMessageInteractionOpened
  );
  emitter.off(BUS_EVENTS.OPEN_MESSAGE_INFO, handleOpenMessageInfo);
});

// Aceleres: o nome de quem falou no grupo agora é renderizado como rótulo da
// bolha, a partir de content_attributes. As mensagens JÁ GRAVADAS, porém,
// carregam esse nome cravado no início do próprio texto ("Bruno:\n…") — três
// ingestões diferentes fizeram isso, cada uma com sua marcação. Sem remover o
// prefixo aqui, o histórico apareceria com o nome duas vezes: no rótulo e no
// corpo.
//
// A limpeza acontece na entrega do contexto, e não em cada bolha, porque texto,
// legenda de mídia, citação e menu de contexto leem todos deste mesmo `content`
// — corrigi-los um a um deixaria alguma superfície para trás.
const displayContent = computed(() =>
  stripGroupSenderPrefix(props.content, props.contentAttributes)
);

provideMessageContext({
  ...toRefs(props),
  content: displayContent,
  canDelete: computed(() => contextMenuEnabledOptions.value.delete),
  isPrivate: computed(() => props.private),
  variant,
  orientation,
  isBotOrAgentMessage,
  shouldGroupWithNext,
});
</script>

<!-- eslint-disable-next-line vue/no-root-v-if -->
<template>
  <div
    v-if="shouldRenderMessage"
    :id="`message${props.id}`"
    class="flex w-full mb-1 message-bubble-container relative group/context-menu"
    :data-message-id="props.id"
    :data-attachment-type="attachments?.[0]?.fileType || 'none'"
    :data-outgoing-image="isOutgoingImageMessage ? 'true' : undefined"
    :class="[
      flexOrientationClass,
      {
        'aceleres-react-open': showQuickReact,
        'aceleres-context-open': showContextMenu,
        'group-with-next': shouldGroupWithNext,
        'bg-n-alpha-1': showBackgroundHighlight,
        'acl-message-selected': isSelected,
        'message--selectable cursor-pointer':
          isSelectionMode && variant !== MESSAGE_VARIANTS.ACTIVITY,
        'has-reactions': !!(
          contentAttributes &&
          contentAttributes.reactions &&
          contentAttributes.reactions.length
        ),
      },
    ]"
    @click.capture="handleMessageClick"
  >
    <div
      v-if="isSelectionMode && variant !== MESSAGE_VARIANTS.ACTIVITY"
      class="acl-message-select-checkbox flex-shrink-0 flex items-center justify-center w-12 self-center"
    >
      <span
        class="size-6 rounded border-2 flex items-center justify-center transition-all shadow-sm"
        :class="
          isSelected
            ? 'bg-n-slate-12 border-n-slate-12'
            : 'border-n-slate-7 bg-n-solid-1 hover:border-n-slate-10'
        "
      >
        <span v-if="isSelected" class="i-lucide-check size-4 text-white" />
      </span>
    </div>
    <div v-if="variant === MESSAGE_VARIANTS.ACTIVITY" class="w-full">
      <ActivityBubble :content="content" />
    </div>
    <div
      v-else
      :class="[
        gridClass,
        {
          'gap-y-2': contentAttributes.externalError,
          'w-full': isSelectionMode || variant === MESSAGE_VARIANTS.EMAIL,
        },
      ]"
      class="gap-x-2"
      :style="{
        gridTemplateAreas: gridTemplate,
      }"
    >
      <div
        v-if="!shouldGroupWithNext && shouldShowAvatar"
        v-tooltip.left-end="avatarTooltip"
        class="acl-message-avatar [grid-area:avatar] flex items-end"
      >
        <Avatar v-bind="avatarInfo" :size="24" />
      </div>
      <div
        class="acl-message-bubble-row [grid-area:bubble] flex pb-2 select-none md:select-text"
        :class="{
          'ltr:ml-8 rtl:mr-8 justify-end': orientation === ORIENTATION.RIGHT,
          'ltr:mr-8 rtl:ml-8': orientation === ORIENTATION.LEFT,
          'min-w-0': variant === MESSAGE_VARIANTS.EMAIL,
          'acl-bubble-tight-pb': !(
            contentAttributes &&
            contentAttributes.reactions &&
            contentAttributes.reactions.length
          ),
        }"
        @contextmenu="openContextMenu($event)"
        @touchstart.passive="onTouchStart"
        @touchend="onTouchEnd"
        @touchcancel="onTouchEnd"
        @touchmove.passive="onTouchMove"
      >
        <div
          class="aceleres-bubble-with-actions relative inline-block max-w-full"
          :class="{
            'acl-outgoing-image-wrapper': isOutgoingImageMessage,
          }"
          :style="{
            marginLeft: isOutgoingImageMessage ? 'auto' : undefined,
            marginRight: isOutgoingImageMessage ? '0' : undefined,
          }"
        >
          <!-- Aceleres: shadow movido pro .left-bubble / .right-bubble via
               filter:drop-shadow (em Message.vue style), pra envolver a ponta
               do balao. Aqui no wrapper interno mantemos so o rounded. -->
          <div
            class="aceleres-bubble-surface relative inline-flex max-w-full rounded-xl"
          >
            <Component
              v-if="!isWameMediaPending"
              :is="componentToRender"
              :class="
                isMessageDeleted
                  ? 'border border-n-weak !bg-transparent text-n-slate-11'
                  : undefined
              "
            >
              <template
                v-if="
                  shouldShowContextMenu && isBubble && hasAdaptiveMediaMenuSlot
                "
                #menu-trigger="{ chevronTone }"
              >
                <button
                  type="button"
                  aria-label="Abrir menu da mensagem"
                  class="aceleres-bubble-chevron"
                  :class="[
                    chevronStyleClasses,
                    adaptiveChevronToneClasses(chevronTone),
                    orientation === 'right' ? 'is-right-bubble' : 'is-left-bubble',
                  ]"
                  @click.stop="openContextMenu($event, $event.currentTarget)"
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="3.4"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    style="pointer-events: none; display: block"
                  >
                    <polyline points="6 9 12 15 18 9"></polyline>
                  </svg>
                </button>
              </template>
            </Component>
            <WameMediaProgress v-else />
            <button
              v-if="
                shouldShowContextMenu &&
                isBubble &&
                !hasAdaptiveMediaMenuSlot &&
                !isStickerAlbumBubble
              "
              type="button"
              aria-label="Abrir menu da mensagem"
              class="aceleres-bubble-chevron"
              :class="[
                chevronStyleClasses,
                orientation === 'right' ? 'is-right-bubble' : 'is-left-bubble',
              ]"
              @click.stop="openContextMenu($event, $event.currentTarget)"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="3.4"
                stroke-linecap="round"
                stroke-linejoin="round"
                style="pointer-events: none; display: block"
              >
                <polyline points="6 9 12 15 18 9"></polyline>
              </svg>
            </button>
          </div>
          <!-- Aceleres: botões de hover ao lado da bolha. Ordem visual esq→dir
               [smile][forward][bubble] (reagir primeiro, encaminhar à direita do
               smile — pedido 2026-07-03). DOM segue a mesma ordem p/ Tab/leitor de
               tela. Ambos position:absolute, então só o foco depende da ordem.
               Suprimidos pro par de stickers (!isStickerAlbumBubble) pelo mesmo
               motivo do chevron acima: agiriam só na ÚLTIMA mensagem do par,
               enganoso pra um card que representa 2 mensagens. -->
          <button
            v-if="shouldShowContextMenu && isBubble && !isStickerAlbumBubble"
            type="button"
            aria-label="Reagir à mensagem"
            class="aceleres-bubble-smile"
            :class="[
              orientation === 'right' ? 'is-right-bubble' : 'is-left-bubble',
              { 'is-active': showQuickReact },
            ]"
            @click.stop="toggleQuickReact($event)"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              style="pointer-events: none; display: block"
            >
              <circle cx="12" cy="12" r="10" />
              <path d="M8 14s1.5 2 4 2 4-2 4-2" />
              <line x1="9" y1="9" x2="9.01" y2="9" />
              <line x1="15" y1="9" x2="15.01" y2="9" />
            </svg>
          </button>
          <button
            v-if="
              shouldShowContextMenu &&
              isBubble &&
              orientation === 'right' &&
              !isStickerAlbumBubble
            "
            type="button"
            aria-label="Encaminhar mensagem"
            class="aceleres-bubble-forward"
            @click.stop="forwardThisMessage"
          >
            <svg
              viewBox="0 0 24 24"
              width="16"
              height="16"
              preserveAspectRatio="xMidYMid meet"
              fill="none"
              style="pointer-events: none; display: block"
            >
              <path
                fill="currentColor"
                d="M19.18 11 15.3 7.13a.97.97 0 0 1-.3-.72c0-.27.1-.51.3-.71.2-.18.44-.27.71-.27.28 0 .5.09.69.27l4.6 4.6c.1.1.17.2.21.32a1.2 1.2 0 0 1 0 .76.88.88 0 0 1-.21.32l-4.6 4.6a.9.9 0 0 1-.7.29c-.27-.01-.5-.1-.7-.29a1 1 0 0 1-.31-.7.87.87 0 0 1 .29-.7l3.9-3.9Zm-6 1H7c-.83 0-1.54.3-2.13.88A2.9 2.9 0 0 0 4 15v3c0 .28-.1.52-.29.71A.94.94 0 0 1 3 19a.97.97 0 0 1-.71-.29A.97.97 0 0 1 2 18v-3c0-1.38.49-2.56 1.46-3.54A4.82 4.82 0 0 1 7 10h6.18L10.3 7.13a.97.97 0 0 1-.3-.72c0-.27.1-.51.3-.71.2-.18.44-.27.71-.27.28 0 .5.09.69.27l4.6 4.6c.1.1.17.2.21.32a1.2 1.2 0 0 1 0 .76.88.88 0 0 1-.21.32l-4.6 4.6a.9.9 0 0 1-.7.29c-.27-.01-.5-.1-.7-.29a1 1 0 0 1-.31-.7.87.87 0 0 1 .29-.7l2.9-2.9Z"
              />
            </svg>
          </button>
          <Teleport to="body">
            <Transition name="aceleres-quick-pill">
              <div
                v-if="showQuickReact"
                ref="quickReactRef"
                class="aceleres-quick-react-pill"
                :class="[
                  `is-${pillDirection}`,
                  { 'is-picker-open': initialShowPicker },
                ]"
                :style="pillStyle"
                @click.stop
              >
                <MessageReactionBar
                  @react="handleQuickReact"
                  @more="openFullPickerFromPill"
                  @close="closeQuickReactAndPicker"
                />
              </div>
            </Transition>
          </Teleport>
        </div>
      </div>
      <MessageReactions
        v-if="
          contentAttributes &&
          contentAttributes.reactions &&
          contentAttributes.reactions.length
        "
        :reactions="contentAttributes.reactions"
        class="[grid-area:reactions] flex -mt-[8px] relative z-10"
        :class="
          orientation === 'right'
            ? 'ltr:ml-8 rtl:mr-8 justify-end'
            : 'ltr:mr-8 rtl:ml-8'
        "
        @open="openReactionsModal"
      />
      <Teleport to="body">
        <MessageReactionsModal
          v-if="showReactionsModal"
          :reactions="
            (contentAttributes && contentAttributes.reactions) || []
          "
          :current-user-id="currentUserId"
          :anchor-el="reactionsAnchorEl"
          :orientation="orientation"
          @react="handleReaction"
          @close="showReactionsModal = false"
        />
      </Teleport>
      <MessageError
        v-if="contentAttributes.externalError"
        class="[grid-area:meta]"
        :class="['acl-message-error', flexOrientationClass]"
        @retry="emit('retry')"
      />
    </div>
    <div v-if="shouldShowContextMenu" class="context-menu-wrap">
      <ContextMenu
        ref="contextMenuRef"
        v-if="isBubble"
        :context-menu-position="contextMenuPosition"
        :is-open="showContextMenu"
        :enabled-options="contextMenuEnabledOptions"
        :message="payloadForContextMenu"
        :selection-messages="contextMenuSelectionMessages"
        hide-button
        :initial-show-picker="initialShowPicker"
        :current-user-id="currentUserId"
        @open="openContextMenu"
        @close="closeContextMenu"
        @reply-to="handleReplyTo"
        @react="handleQuickReact"
      />
    </div>
  </div>
</template>

<style lang="scss">
/* Aceleres: cantos superiores sempre arredondados (mesmo em mensagens
   agrupadas), pra todas as bolhas ficarem com cantos top como os outros. */
.group-with-next + .message-bubble-container {
  .left-bubble {
    @apply ltr:rounded-tl-xl rtl:rounded-tr-xl;
  }

  .right-bubble {
    @apply ltr:rounded-tr-xl rtl:rounded-tl-xl;
  }
}

/* Aceleres: bico de balao tipo WhatsApp/ChatGPT no canto inferior do bubble.
   Em bubbles de midia (Image/Video/etc) o BaseBubble vem com overflow-hidden;
   sobrepomos pra que o ::after possa sair pra fora do bubble. Os elementos
   internos (imagens etc) ja vem com rounded proprio, entao nao quebra layout. */
.left-bubble,
.right-bubble {
  overflow: visible !important;
}
.aceleres-bubble-surface {
  overflow: visible !important;
}
/* Aceleres: shadow envolvendo a forma INTEIRA do balao (incluindo o bico do
   ::after). filter:drop-shadow segue os pixels visiveis — box-shadow nao,
   por isso a sombra fica retangular e desconectada da ponta. Aplicado em
   ambas bolhas pra manter o mesmo padrao visual. */
.left-bubble,
.right-bubble {
  filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.08))
    drop-shadow(0 4px 12px rgba(0, 0, 0, 0.08));
}
.left-bubble::after,
.right-bubble::after {
  content: '';
  position: absolute;
  background-color: inherit;
  pointer-events: none;
  width: 7px;
  height: 12px;
}
/* LEFT bubble: bico no canto SUPERIOR esquerdo (msg recebida).
   border-bottom-right-radius arredonda a quina inferior interna do pseudo,
   deixando o canto top-left bem agudo (a "ponta" do bico). Encaixado no
   canto top-left da bolha (rounded-tl zerado). */
.left-bubble {
  position: relative !important;
  overflow: visible !important;
  border-top-left-radius: 8px !important;
}
.left-bubble::after {
  width: 12px !important;
  height: 14px !important;
  background-color: #fff !important;
  left: -7px !important;
  top: 0 !important;
  z-index: 3 !important;
  pointer-events: none !important;
  clip-path: polygon(100% 0, 100% 100%, 0 0) !important;
}
/* RIGHT bubble: bico espelhado no canto SUPERIOR direito (msg enviada),
   alinhado ao visual do bubble verde principal. */
.right-bubble {
  border-top-right-radius: 0 !important;
  border-bottom-right-radius: 12px !important;
  font-size: 0.975rem;
}
.right-bubble::after {
  width: 24px !important;
  height: 12px !important;
  top: 0 !important;
  bottom: auto !important;
  right: -12px !important;
  left: auto !important;
  z-index: 3 !important;
  pointer-events: none !important;
  border-top-left-radius: 0 !important;
  clip-path: polygon(0 0, 50% 100%, 100% 12%, 100% 0) !important;
}
/* Aceleres: 12px de respiro entre stickers, contados a partir do HORARIO
   sobreposto no canto do sticker — nao da borda da imagem em si (o horario
   fica ~16.5px acima da borda real, entao medir a partir da imagem sempre
   sobra espaco demais). Ver Base.vue: pra bolha de sticker o horario cola
   na quina (`bottom-0` em vez do `bottom-2.5` padrao), o que deixa so 4px
   entre o texto do horario e a borda da bolha.

   Contas verificadas rendendo o CSS compilado de verdade + stickers reais
   (nao so lendo o Tailwind): com o horario colado, falta 8px de fora pra
   fechar 12px a partir dele. Desses 8px, 4px ja vem do padding-bottom da
   `.acl-message-bubble-row` (`.acl-bubble-tight-pb`, 0.2rem); os outros
   4px sao este margin-bottom.

   Vai em PIXEL puro de proposito, sem `calc()`: o build (cssnano/postcss-
   calc) reduz `calc(12px - 0.2rem)` errado, descartando o termo em rem e
   deixando so `12px` no CSS final — silencioso, sem warning. Reaproveita
   o `[data-bubble-name='sticker']` que o Image.vue ja emite — mesmo
   seletor da regra do halo acima —, entao nao ha classe nova nem marcacao
   duplicada.

   Fica ANTES do `.has-reactions` de proposito: os dois seletores tem a
   mesma especificidade (0,2,0), entao a ordem decide — sticker com reacao
   continua com os 0.8rem que a chip de reacao precisa. */
.message-bubble-container:has([data-bubble-name='sticker']) {
  margin-bottom: 4px;
}
/* Aceleres: espaco extra abaixo de mensagens com emoji de reacao — pra
   que a chip de reacao nao cole no balao da proxima mensagem. */
.message-bubble-container.has-reactions {
  margin-bottom: 0.8rem;
}
/* Aceleres: quando NAO tem reacao, o wrapper [grid-area:bubble] vai com
   padding-bottom mais apertado pra mensagens ficarem mais compactas. */
.acl-bubble-tight-pb {
  padding-bottom: 0.2rem !important;
}

/* Aceleres: halo radial atras da flechinha. O pseudo cobre a bolha
   INTEIRA (inset:0 + border-radius:inherit), e o gradient e definido com
   `radial-gradient(circle Xpx at <chevron-center>, ...)` — ou seja, uma
   forma circular nativa que se esvai pra transparente progressivamente.
   Como o pseudo cobre todo o card e o gradiente termina em transparent,
   nao ha bordas/retangulos visiveis em lugar nenhum. */
.left-bubble::before,
.right-bubble::before {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: inherit;
  opacity: 0;
  transition: opacity 0.18s ease;
  pointer-events: none;
  z-index: 10;
}
/* No left-bubble (branco) o halo e branco translucido sutil. */
.left-bubble::before {
  background: radial-gradient(
    circle 70px at calc(100% - 19px) 17px,
    rgba(255, 255, 255, 0.7) 0%,
    rgba(255, 255, 255, 0.35) 35%,
    rgba(255, 255, 255, 0.12) 65%,
    transparent 100%
  );
}
/* No right-bubble (verde #d9fdd3) o halo usa o mesmo verde claro da
   propria bolha, so que mais concentrado, completamente circular e
   com fade gradativo pra transparent — nenhum corte/retangulo. */
.right-bubble::before {
  background: radial-gradient(
    circle 70px at calc(100% - 19px) 17px,
    rgba(217, 253, 211, 0.9) 0%,
    rgba(217, 253, 211, 0.5) 35%,
    rgba(217, 253, 211, 0.2) 65%,
    transparent 100%
  );
}
.aceleres-bubble-surface:hover .left-bubble::before,
.aceleres-bubble-surface:hover .right-bubble::before {
  opacity: 1;
}
/* Aceleres: em bolhas com midia (imagem, video, audio, embed, anexo, ig-story)
   o halo do ::before pintava por cima do conteudo — deixa um brilho branco/
   verde no canto sup-direito da propria imagem/video. Como nao ha texto pra
   contrastar, desativa o halo nesses casos. Bolhas de texto/csat/unsupported
   continuam com o halo normal. */
.left-bubble[data-bubble-name='image']::before,
.left-bubble[data-bubble-name='video']::before,
.left-bubble[data-bubble-name='audio']::before,
.left-bubble[data-bubble-name='embed']::before,
.left-bubble[data-bubble-name='attachment']::before,
.left-bubble[data-bubble-name='ig-story']::before,
.left-bubble[data-bubble-name='gif']::before,
.left-bubble[data-bubble-name='sticker']::before,
.left-bubble[data-bubble-name='sticker-album']::before,
.right-bubble[data-bubble-name='image']::before,
.right-bubble[data-bubble-name='video']::before,
.right-bubble[data-bubble-name='audio']::before,
.right-bubble[data-bubble-name='embed']::before,
.right-bubble[data-bubble-name='attachment']::before,
.right-bubble[data-bubble-name='ig-story']::before,
.right-bubble[data-bubble-name='gif']::before,
.right-bubble[data-bubble-name='sticker']::before,
.right-bubble[data-bubble-name='sticker-album']::before {
  display: none;
}

/* Aceleres: flechinha no canto sup-direito da bolha. Estilo WhatsApp:
   um pequeno patch translúcido encostado no topo direito da bolha — dá
   sensação de "vir de dentro da bolha" e fica sutil sem chamar atenção. */
.aceleres-bubble-chevron {
  position: absolute;
  top: 6px;
  right: 8px;
  z-index: 20;
  width: 24px;
  height: 24px;
  background: transparent;
  color: rgba(0, 0, 0, 0.87);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  visibility: hidden;
  pointer-events: none;
  cursor: pointer;
  border: none;
  border-radius: 0;
  padding: 0;
  transition:
    opacity 0.12s,
    visibility 0.12s,
    filter 0.12s;
}
/* Igual ao WhatsApp: a seta aparece apenas sobre o card da mensagem. */
.aceleres-bubble-surface:hover .aceleres-bubble-chevron,
.aceleres-bubble-chevron:focus-visible {
  opacity: 1;
  visibility: visible;
  pointer-events: auto;
  outline: none;
}

/* Aceleres: wrapper relativo que abriga o bubble + o smile (que fica fora
   do `.inline-flex` do bubble). Inline-block pra não esticar a row. */
div.\[grid-area\:bubble\] > .aceleres-bubble-with-actions {
  max-width: 50% !important;
}

/* Aceleres: imagens enviadas devem manter o wrapper no tamanho real do
   bubble. Se o wrapper ocupa a coluna inteira, os controles absolutos de
   reação/encaminhamento se ancoram longe da imagem. */
@media (min-width: 768px) {
  .acl-outgoing-image-wrapper {
    display: inline-flex !important;
    width: fit-content !important;
    max-width: min(50%, 408px) !important;
    flex: 0 0 auto !important;
    align-items: flex-start !important;
  }

  .acl-outgoing-image-wrapper > .aceleres-bubble-surface {
    display: inline-flex !important;
    width: fit-content !important;
    max-width: 100% !important;
    flex: 0 0 auto !important;
  }
}

.aceleres-bubble-with-actions {
  /* nada além do relative/inline-block — só serve de âncora pro smile */
}

/* Aceleres: mensagem selecionada no modo encaminhar — fundo verde-WhatsApp
   translúcido ocupando toda a linha (full-bleed: margin -16px compensa o
   px-4 do <ul> pai no MessageList; padding 16px reposiciona o conteúdo).
   align-items center alinha o checkbox com o bubble verticalmente. */
.acl-message-selected {
  background: rgba(0, 128, 105, 0.08);
  margin-left: -16px;
  margin-right: -16px;
  padding-left: 16px;
  padding-right: 16px;
  align-items: center;
}

@media (max-width: 767px) {
  .acl-message-select-checkbox {
    display: none !important;
  }

  .acl-whatsapp-mobile-scope .acl-message-selected {
    margin-right: -8px !important;
    margin-left: -8px !important;
    padding-right: 12px !important;
    padding-left: 12px !important;
    background: rgba(0, 128, 105, 0.16) !important;
  }
}
/* Modo seleção: esconder os botões de reação (smile) e encaminhar (forward)
   que apareceriam no hover da bolha. */
.message--selectable .aceleres-bubble-smile,
.message--selectable .aceleres-bubble-forward {
  display: none !important;
}

/* Aceleres: smile redondo FORA do bubble, do lado oposto à orientação:
   à esquerda para bubble outgoing (right-bubble), à direita para incoming
   (left-bubble). Aparece no hover da bolha — clica e abre a pill rápida. */
.aceleres-bubble-smile {
  position: absolute;
  top: 50%;
  z-index: 20;
  width: 26px;
  height: 26px;
  background: rgba(255, 255, 255, 0.96);
  color: rgba(0, 0, 0, 0.62);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  cursor: pointer;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 50%;
  padding: 0;
  transition:
    opacity 0.12s,
    transform 0.12s,
    filter 0.12s,
    background 0.12s;
  box-shadow:
    0 2px 6px rgba(0, 0, 0, 0.14),
    0 1px 2px rgba(0, 0, 0, 0.08);
  transform: translateY(-50%);
}
/* Bubble outgoing (à direita) → smile na ESQUERDA da bolha.
   Aceleres 2026-07-03: ordem visual [smile][forward][bubble] (WhatsApp-like:
   reagir primeiro, encaminhar à direita do smile). O smile fica mais afastado
   da bolha; o forward cola nele pelo lado interno. */
.aceleres-bubble-smile.is-right-bubble {
  right: 100%;
  left: auto;
  margin-right: 46px;
  z-index: 25;
}
/* Bubble incoming (à esquerda) → smile na DIREITA da bolha. */
.aceleres-bubble-smile.is-left-bubble {
  left: 100%;
  right: auto;
  margin-left: 14px;
  z-index: 25;
}
.message-bubble-container:hover .aceleres-bubble-smile,
.aceleres-bubble-smile:hover,
.aceleres-bubble-smile:focus,
.aceleres-bubble-smile.is-active {
  opacity: 1;
  outline: none;
}
.aceleres-bubble-smile:hover,
.aceleres-bubble-smile.is-active {
  transform: translateY(-50%) scale(1.08);
  background: white;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2));
}
.dark .aceleres-bubble-smile {
  background: rgba(40, 40, 40, 0.96);
  color: rgba(255, 255, 255, 0.8);
  border-color: rgba(255, 255, 255, 0.1);
}
.dark .aceleres-bubble-smile:hover,
.dark .aceleres-bubble-smile.is-active {
  background: rgba(60, 60, 60, 0.98);
}

/* Aceleres: seta de encaminhar (forward) ao lado do smile — SÓ outgoing
   (right-bubble). Mesmo visual redondo do smile. Fica imediatamente À DIREITA
   do smile (entre smile e bolha): ordem [smile][forward][bubble]. Aparece no
   hover. (pedido 2026-07-03 — antes era [forward][smile]). */
.aceleres-bubble-forward {
  position: absolute;
  top: 50%;
  right: 100%;
  margin-right: 14px; /* cola no lado esquerdo da bolha; smile fica 26px+6px além */
  z-index: 25;
  width: 26px;
  height: 26px;
  background: rgba(255, 255, 255, 0.96);
  color: rgba(0, 0, 0, 0.62);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  cursor: pointer;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 50%;
  padding: 0;
  transition:
    opacity 0.12s,
    transform 0.12s,
    filter 0.12s,
    background 0.12s;
  box-shadow:
    0 2px 6px rgba(0, 0, 0, 0.14),
    0 1px 2px rgba(0, 0, 0, 0.08);
  transform: translateY(-50%);
}
.message-bubble-container:hover .aceleres-bubble-forward,
.aceleres-bubble-forward:hover,
.aceleres-bubble-forward:focus {
  opacity: 1;
  outline: none;
}
.aceleres-bubble-forward:hover {
  transform: translateY(-50%) scale(1.08);
  background: white;
  color: #15603e;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2));
}
.dark .aceleres-bubble-forward {
  background: rgba(40, 40, 40, 0.96);
  color: rgba(255, 255, 255, 0.8);
  border-color: rgba(255, 255, 255, 0.1);
}
.dark .aceleres-bubble-forward:hover {
  background: rgba(60, 60, 60, 0.98);
}

/* Aceleres: pill rápida de reação (estilo WhatsApp). Aparece ACIMA do botão
   smile, centralizada e com gap mínimo. 6 emojis + "+" — o "+" abre o
   picker completo. */

/* Aceleres: a quick-react-pill agora é <Teleport to="body"> + position:fixed
   (mesmo padrão do emoji-picker). As coords (left/top/bottom) vêm inline do
   positionQuickReactPill(), calculadas a partir do rect do smile — então este
   bloco só define o visual; NÃO posiciona. z-index alto (abaixo só do topbar
   9999/picker 9999) garante que ela abre por cima de tudo. (2026-07-02) */
.message-bubble-container.aceleres-react-open {
  z-index: 40;
}
.message-bubble-container.aceleres-context-open {
  z-index: 39;
  background: rgba(0, 128, 105, 0.055);
  border-radius: 6px;
}
.dark .message-bubble-container.aceleres-context-open {
  background: rgba(255, 255, 255, 0.055);
}

.aceleres-quick-react-pill {
  position: fixed;
  z-index: 9998;
  background: white;
  border-radius: 50px;
  box-shadow:
    0 6px 16px rgba(0, 0, 0, 0.18),
    0 2px 4px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(0, 0, 0, 0.06);
  padding: 1px 2px;
  display: flex;
  align-items: center;
  transform: translateX(-50%);
  will-change: transform, opacity;
}
/* Aceleres: ícones de reação rápida (emoji + botão "+") em tamanho normal
   dentro da pill — botões de 24px com emoji de 1.75rem. Botão "+" com ícone plus limpo
   (i-lucide-plus) e SEM background (nem no hover) — só o ícone.
   (pedido 2026-07-01) */
.aceleres-quick-react-pill :deep(.aceleres-emoji-btn) {
  width: 1.75rem !important;
  height: 1.75rem !important;
  font-size: 0 !important;
  overflow: visible !important;
}
.aceleres-quick-react-pill
  :deep(.aceleres-emoji-btn:not(.aceleres-emoji-btn--more) span) {
  width: 2rem !important;
  height: 2rem !important;
  font-size: 2rem !important;
}
.aceleres-quick-react-pill
  :deep(.aceleres-emoji-btn:not(.aceleres-emoji-btn--more) .cw-emoji) {
  display: block !important;
  width: 2rem !important;
  height: 2rem !important;
}
.aceleres-quick-react-pill :deep(.aceleres-emoji-btn--more span) {
  width: 1rem !important;
  height: 1rem !important;
  font-size: 1rem !important;
}
.aceleres-quick-react-pill :deep(.aceleres-emoji-btn--more),
.aceleres-quick-react-pill :deep(.aceleres-emoji-btn--more:hover) {
  background: transparent !important;
  background-color: transparent !important;
}
/* Aceleres: a pill aberta acima da mensagem precisa de reações mais legíveis. */
.aceleres-quick-react-pill.is-up :deep(.aceleres-emoji-btn) {
  width: 3.5rem !important;
  height: 3.5rem !important;
}
.aceleres-quick-react-pill.is-up
  :deep(.aceleres-emoji-btn:not(.aceleres-emoji-btn--more) span),
.aceleres-quick-react-pill.is-up
  :deep(.aceleres-emoji-btn:not(.aceleres-emoji-btn--more) .cw-emoji) {
  width: 2.5rem !important;
  height: 2.5rem !important;
  font-size: 2.5rem !important;
}
.aceleres-quick-react-pill.is-up :deep(.aceleres-emoji-btn--more span) {
  width: 1.5rem !important;
  height: 1.5rem !important;
  font-size: 1.5rem !important;
}
/* A pill é teleported + fixed; `left` (vindo inline) = centro X do smile.
   O `transform: translateX(-50%)` centra a pill nesse ponto — independe da
   largura medida, então fica sempre perfeitamente centralizada no smile.
   A pill abre acima do botão e cresce a partir da base. */
.aceleres-quick-react-pill.is-up {
  transform-origin: bottom center;
}
.aceleres-quick-react-pill.is-down {
  transform-origin: top center;
}
.aceleres-quick-react-pill.is-picker-open {
  pointer-events: none;
  z-index: 9997;
}
.aceleres-quick-react-pill.is-picker-open :deep(.aceleres-emoji-btn) {
  pointer-events: auto;
}
.dark .aceleres-quick-react-pill {
  background: #2a2a2a;
  border-color: rgba(255, 255, 255, 0.08);
}
.aceleres-quick-pill-enter-active,
.aceleres-quick-pill-leave-active {
  transition:
    opacity 0.14s ease,
    transform 0.16s cubic-bezier(0.16, 1, 0.3, 1);
}
.aceleres-quick-pill-enter-from,
.aceleres-quick-pill-leave-to {
  opacity: 0;
  transform: translateX(-50%) scale(0.86) translateY(4px);
}
.aceleres-quick-pill-enter-from.is-down,
.aceleres-quick-pill-leave-to.is-down {
  transform: translateX(-50%) scale(0.86) translateY(-4px);
}

@media (hover: none), (pointer: coarse) {
  .acl-message-bubble-row {
    -webkit-touch-callout: none;
  }

  .aceleres-bubble-smile,
  .aceleres-bubble-forward,
  .aceleres-bubble-chevron {
    display: none !important;
  }
}

@media (max-width: 380px) {
  .aceleres-quick-react-pill {
    max-width: calc(100vw - 16px);
    padding-right: 2px;
    padding-left: 2px;
  }

  .aceleres-quick-react-pill :deep(.aceleres-emoji-btn) {
    width: 1.75rem !important;
    height: 1.75rem !important;
    font-size: 0 !important;
  }

  .aceleres-quick-react-pill.is-up :deep(.aceleres-emoji-btn) {
    width: calc((100vw - 26px) / 7) !important;
    height: calc((100vw - 26px) / 7) !important;
  }

  .aceleres-quick-react-pill.is-up
    :deep(.aceleres-emoji-btn:not(.aceleres-emoji-btn--more) span),
  .aceleres-quick-react-pill.is-up
    :deep(.aceleres-emoji-btn:not(.aceleres-emoji-btn--more) .cw-emoji) {
    width: calc((100vw - 26px) / 9) !important;
    height: calc((100vw - 26px) / 9) !important;
    font-size: calc((100vw - 26px) / 9) !important;
  }
}

@media (prefers-reduced-motion: reduce) {
  .aceleres-quick-pill-enter-active,
  .aceleres-quick-pill-leave-active,
  .aceleres-emoji-btn {
    transition-duration: 0.01ms !important;
  }
}
</style>
